import { Link } from "@inertiajs/react"
import { FileStack, Lock, Shield, Zap } from "lucide-react"

import { PublicFooter } from "@/components/public-footer"
import { PublicHeader } from "@/components/public-header"
import { SEO } from "@/components/seo"

const values = [
  {
    icon: Shield,
    title: "Privacy First",
    description:
      "Your files stay in your browser. Most operations never touch our servers.",
  },
  {
    icon: Zap,
    title: "Fast & Simple",
    description: "No bloated software. Upload, edit, download.",
  },
  {
    icon: Lock,
    title: "Secure by Design",
    description:
      "Server-processed files are encrypted and deleted immediately after.",
  },
  {
    icon: FileStack,
    title: "Free to Use",
    description: "Core tools stay free. Paid plans exist for power users.",
  },
]

export default function About() {
  return (
    <>
      <SEO
        title="About StitchPDF | Free Online PDF Tools"
        description="StitchPDF is a privacy-focused PDF toolkit. Our free tools process files in your browser - no uploads required for most operations."
        canonical="https://stitchpdf.com/about"
        ogTitle="About StitchPDF"
        ogDescription="Privacy-focused PDF tools that work in your browser."
      >
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            mainEntity: {
              "@type": "Organization",
              name: "StitchPDF",
              description:
                "Privacy-focused PDF tools that process files directly in your browser.",
              url: "https://stitchpdf.com",
              foundingDate: "2024",
              founder: {
                "@type": "Organization",
                name: "Gorg Rails Tech Solutions, Limitada",
                address: {
                  "@type": "PostalAddress",
                  addressCountry: "PT",
                },
              },
            },
          })}
        </script>
      </SEO>

      <div className="bg-background flex min-h-screen flex-col">
        <PublicHeader />

        <main className="container mx-auto flex-1 px-4 py-16">
          <div className="mx-auto max-w-3xl">
            <h1 className="text-4xl font-bold">About StitchPDF</h1>
            <p className="text-muted-foreground mt-4 text-lg">
              Simple, private PDF tools that respect your data
            </p>

            <div className="prose dark:prose-invert mt-12 max-w-none">
              <h2>Why We Built This</h2>
              <p>
                Most PDF tools upload your files to unknown servers or bury
                simple functionality behind confusing interfaces. We wanted
                something simpler.
              </p>
              <p>
                StitchPDF processes your files in your browser. Your documents
                stay on your device. Password protection requires server-side
                encryption, but everything else runs locally.
              </p>

              <h2>How It Works</h2>
              <p>
                Tools like{" "}
                <Link
                  href="/merge-pdf"
                  className="text-primary hover:underline"
                >
                  Merge
                </Link>
                ,{" "}
                <Link
                  href="/split-pdf"
                  className="text-primary hover:underline"
                >
                  Split
                </Link>
                , and{" "}
                <Link href="/sign-pdf" className="text-primary hover:underline">
                  Sign
                </Link>{" "}
                run JavaScript libraries in your browser. Your device does the
                work.
              </p>
              <p>
                You get faster processing (no upload wait), real privacy (we
                never see your files), and offline capability.
              </p>

              <h2>Who We Are</h2>
              <p>
                <strong>Gorg Rails Tech Solutions, Limitada</strong> builds and
                operates StitchPDF from Portugal. We build web applications
                focused on user experience and data privacy.
              </p>
              <p>
                See what our users say on{" "}
                <a
                  href="https://www.provenexpert.com/stitchpdf/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  ProvenExpert
                </a>
                .
              </p>

              <h2>Free and Paid</h2>
              <p>
                Core tools stay free. Paid plans add more daily operations and
                permanent file storage for power users.
              </p>
              <p>
                We do not sell data, track documents, or show ads. Revenue comes
                from optional paid plans.
              </p>
            </div>

            <div className="mt-16 grid gap-8 sm:grid-cols-2">
              {values.map((value) => (
                <div key={value.title} className="flex gap-4">
                  <div className="bg-primary/10 flex h-12 w-12 shrink-0 items-center justify-center rounded-lg">
                    <value.icon
                      className="text-primary h-6 w-6"
                      aria-hidden="true"
                    />
                  </div>
                  <div>
                    <h3 className="font-semibold">{value.title}</h3>
                    <p className="text-muted-foreground mt-1 text-sm">
                      {value.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-16 rounded-lg border p-6">
              <h2 className="text-xl font-semibold">Have Questions?</h2>
              <p className="text-muted-foreground mt-2">
                Check out our{" "}
                <Link href="/faq" className="text-primary hover:underline">
                  FAQ page
                </Link>{" "}
                for answers to common questions, or review our{" "}
                <Link href="/privacy" className="text-primary hover:underline">
                  Privacy Policy
                </Link>{" "}
                to learn more about how we protect your data.
              </p>
            </div>
          </div>
        </main>

        <PublicFooter />
      </div>
    </>
  )
}
